/** @jsxImportSource ../../i18n */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ScrollText } from 'lucide-react';
import api from '../../services/api';
import { formatDate } from '../../theme/format';
import { Badge, Button, Card, CardHeader, SkeletonText } from '../ui';
import { DigestLines, digestSignature } from './DigestLines';

const todayParis = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date());

// Dashboard card: the latest daily report in five lines, signed by the
// rapporteur. Read-only (never triggers a generation).
export const DigestCard = ({ className }) => {
  const [digest, setDigest] = useState(undefined);

  useEffect(() => {
    let alive = true;
    api.get('/analysis/morning-digest')
      .then((res) => { if (alive) setDigest(Array.isArray(res?.data?.lines) ? res.data : null); })
      .catch(() => { if (alive) setDigest(null); });
    return () => { alive = false; };
  }, []);

  if (digest === null) {
    return (
      <Card className={className} data-testid="digest-card">
        <CardHeader icon={ScrollText} title="L’essentiel du jour" description="Le rapport quotidien, résumé en cinq lignes." />
        <p className="text-sm text-fg-muted">Aucun rapport pour le moment : le premier arrive avec la prochaine analyse matinale.</p>
      </Card>
    );
  }

  const date = digest?.report_date ? String(digest.report_date).slice(0, 10) : null;
  const signature = digest && digestSignature(digest);
  return (
    <Card tone="gold" className={className} data-testid="digest-card">
      <CardHeader
        icon={ScrollText}
        title="L’essentiel du jour"
        description={date ? `Rapport du ${formatDate(date, 'long')}` : 'Le rapport quotidien, résumé en cinq lignes.'}
        actions={(
          <>
            {date && date !== todayParis() && <Badge>Dernier rapport</Badge>}
            {digest && <Button as={Link} to={`/reports/${digest.report_id}`} variant="link" trailingIcon={ArrowRight}>Lire le rapport</Button>}
          </>
        )}
      />
      {digest === undefined ? <SkeletonText lines={5} /> : (
        <>
          {digest.lines.length > 0
            ? <DigestLines lines={digest.lines} />
            : <p className="text-sm text-fg-muted">Ce rapport ne contient pas de synthèse exploitable.</p>}
          {signature && <p className="mt-4 text-right font-display text-sm italic text-fg-muted">— {signature}</p>}
        </>
      )}
    </Card>
  );
};
